import React from "react";
import { Link } from "react-router-dom";
import "../Style/services.css";

const ServicesHome = () => {
  return (
    <>
      <section className="contentBox">
        <h1>Our Services</h1>
        <p>
          We provide comprehensive solutions for modern cloud-native infrastructure, identity and access management,
          software architecture, security and kubernetes. 
        </p>
      </section>

      <div className="thin-divider"> </div>

      <section className="contentBox" id="topic-identity-access-management">
        <h1>Identity and Access Management</h1>
        <p>
          <Link
            to="/services/case-studies/identity-access-rollout"
            className="services-case-study-link"
          >
            Case study: Lease Car Company SSO
          </Link>
          <br />
          <Link
            to="/services/case-studies/spiffe-in-oauth-2-0"
            className="services-case-study-link"
          >
            Case study: SPIFFE in OAuth 2.0, authorizing cross-domain services
          </Link>
          <br />
          <Link
            to="/services/case-studies/keycloak-dynamic-client-registration-rfc7591"
            className="services-case-study-link"
          >
            Case study: Dynamic Client Registration (RFC 7591) with Keycloak, relevant for your MCP servers
          </Link>
        </p>
        <p>
          A single umbrella for everything identity-related: human access (SSO, directories,
          OAuth/OIDC), cryptographic trust (PKI), and workload or agent identity (SPIFFE/SPIRE). We
          help you design authentication, authorization, and lifecycle so people, services, and
          autonomous components are all covered consistently.
        </p>

        <h2>Keycloak Integration</h2>
        <p>
          Keycloak is an open-source Identity and Access Management (IAM) solution that provides
          single sign-on, social login, user federation, and more. It supports standard protocols
          like OpenID Connect, OAuth 2.0, and SAML.
        </p>
        <ul>
          <li>Single Sign-On (SSO) across all your applications</li>
          <li>Social login integration (Google, GitHub, Microsoft, etc.)</li>
          <li>Role-based access control (RBAC)</li>
          <li>User federation with LDAP and Active Directory</li>
          <li>Multi-factor authentication (MFA)</li>
        </ul>

        <h2>OAuth 2.0, 2.1 and OpenID Connect</h2>
        <p>
          Implementation of industry-standard protocols for secure API authorization and user
          authentication across distributed systems.
          We provide implementation of OAuth 2.0, 2.1 and OpenID Connect. We also provide implementation of OAuth 2.0 Authorization Server and OpenID Connect Provider.
          <ul>
            <li> Authorization Server (Keycloak, OpenID Connect Provider)</li>
            <li> Client (OAuth 2.0, 2.1 and OpenID Connect)</li>
            <li> Token Introspection (RFC 7662)</li>
            <li> Token Exchange (RFC 8693)</li>
            <li> Token Revocation (RFC 7009)</li>
            <li> Clietn Credentials (RFC 6749)</li>
            <li> Client Authentication (RFC 6749)</li>
          </ul>
        </p>

        <h2>LDAP &amp; Active Directory</h2>
        <p>
          Integration with enterprise directory services for centralized user management and
          seamless authentication with existing corporate infrastructure.
        </p>

        <h2>Public Key Infrastructure</h2>
        <p>
          PKI design for issuing, rotating, and revoking certificates across machines, services, and
          people—without turning into an operational burden.
        </p>
        <ul>
          <li>Root and intermediate CA hierarchy, HSM or key protection options</li>
          <li>Certificate lifecycle automation and renewal</li>
          <li>mTLS, code signing, and document or email encryption where applicable</li>
          <li>Monitoring, CRL/OCSP, and incident response for key compromise</li>
        </ul>

        <h2>Identity for LLM agentic services (SPIFFE &amp; SPIRE)</h2>
        <p>
          Cryptographic workload identity for autonomous or semi-autonomous agents and the services
          they call: short-lived, verifiable identities instead of shared secrets or ad-hoc API
          keys—aligned with how you govern human IAM and PKI-backed trust.
        </p>
        <ul>
          <li>SPIFFE IDs and X.509 or JWT SVIDs for agents, tools, and backends</li>
          <li>SPIRE deployment patterns (Kubernetes, VMs, hybrid)</li>
          <li>Policy for which identities may invoke models, tools, or data APIs</li>
          <li>Audit trails and least-privilege across agentic workflows</li>
        </ul>
      </section>

      <div className="thin-divider"> </div>

      <section className="contentBox" id="topic-kubernetes-cloud">
        <h1>Kubernetes & Cloud Infrastructure</h1>
        <p>
          <Link
            to="/services/case-studies/kubernetes-platform-reliability"
            className="services-case-study-link"
          >
            Case study: Multi-cluster SRE and observability
          </Link>
        </p>
        <p>
          Design, deployment, and management of containerized applications on Kubernetes. We help you
          build scalable, resilient cloud-native infrastructure.
        </p>
        <ul>
          <li>Kubernetes cluster setup and management</li>
          <li>CI/CD pipeline implementation</li>
          <li>Infrastructure as Code (Terraform, Pulumi)</li>
          <li>Service mesh configuration (Istio, Linkerd)</li>
          <li>Monitoring and observability (Prometheus, Grafana)</li>
        </ul>
      </section>

      <div className="thin-divider"> </div>

      <section className="contentBox">
        <h1>AI/LLM Integration</h1>
        <p>
          Secure integration of Large Language Models into your enterprise infrastructure with
          proper access controls, audit logging, and compliance measures.
        </p>
        <ul>
          <li>LLM gateway and proxy setup</li>
          <li>Query auditing and compliance</li>
          <li>Sandboxed execution environments</li>
          <li>Cost optimization and rate limiting</li>
        </ul>
      </section>

      <div className="thin-divider"> </div>

      <section className="contentBox">
        <h1>Design of Infrastructure as Code</h1>
        <p>
          End-to-end IaC design: reproducible environments, reviewable changes, and alignment
          between teams, pipelines, and cloud or on-prem targets.
        </p>
        <ul>
          <li>Module layout, naming, and state management (Python, Terraform, Helm, Ansible)</li>
          <li>Environment promotion (dev, staging, production) and policy as code</li>
          <li>Integration with CI/CD and drift detection</li>
          <li>Documentation and handover for long-term maintainability</li>
        </ul>
      </section>

      <div className="thin-divider"> </div>

      <section className="contentBox">
        <h1>Kubernetes Optimization and Automation</h1>
        <p>
          Practical automation for clusters: right-sizing workloads, scheduling, and purchasing models
          so you pay for capacity you actually use. We use large language models to automate the deployment of kubernetes clusters and the management of the clusters.
        </p>
        <ul>
          <li>Resource requests, limits, and vertical versus horizontal scaling</li>
          <li>Node pools, spot or preemptible instances, and autoscaling policies</li>
          <li>Observability for cost drivers (namespaces, teams, applications)</li>
          <li>Recommendations for storage, networking, and idle workload reduction</li>
        </ul>
      </section>

      <div className="thin-divider"> </div>

      <section className="contentBox">
        <h1>Domain-Driven Design for Software Architecture</h1>
        <p>
          Structuring complex systems around the business domain: clear boundaries, shared language,
          and architectures that stay understandable as they grow.
        </p>
        <ul>
          <li>Bounded contexts, context maps, and integration patterns</li>
          <li>Strategic design workshops and ubiquitous language</li>
          <li>Aligning aggregates, domain events, and APIs with real workflows</li>
          <li>Guidance for monolith-to-modular or service extraction roadmaps</li>
        </ul>
      </section>

      <div className="thin-divider"> </div>

      <section className="contentBox">
        <h1>Jakarta EE Design</h1>
        <p>
          Enterprise Java applications built on Jakarta EE (and related standards): robust,
          portable, and maintainable server-side design.
        </p>
        <ul>
          <li>Layering, packaging, and dependency boundaries (CDI, JPA, JAX-RS, etc.)</li>
          <li>Transactions, concurrency, and messaging where appropriate</li>
          <li>Interoperability with MicroProfile and cloud-native runtimes</li>
          <li>Security, configuration, and operational concerns for long-lived systems</li>
        </ul>
      </section>
    </>
  );
};

export default ServicesHome;
